import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Divider,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  LocalFireDepartment as HotIcon,
  Work as WorkIcon,
  Phone as PhoneIcon,
  Block as BlockIcon,
  CheckCircle as ExportedIcon,
  Edit as EditIcon,
  AddCircleOutline as AddStatusIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Handshake as ClientIcon,
  BusinessCenter as CompetitorIcon,
  RemoveCircleOutline as RemoveStatusIcon,
  Storefront as RetailIcon,
  SupervisorAccount as KamIcon
} from '@mui/icons-material';
import { createClickableAddressData } from '../utils/mapUtils';
import { setCompanyStatus, deleteCompanyStatus, getCompanyIdentifier } from '../services/api';

// Конфигурация статусов (должны совпадать с бэкендом)
const STATUS_CONFIG = {
  in_work: { label: 'В работе', color: '#2196f3', bgColor: '#e3f2fd', icon: '🔄', MuiIcon: WorkIcon },
  to_contact: { label: 'Связаться', color: '#ff9800', bgColor: '#fff3e0', icon: '📞', MuiIcon: PhoneIcon },
  hot: { label: 'Горячий', color: '#f44336', bgColor: '#ffebee', icon: '🔥', MuiIcon: HotIcon },
  exported_to_crm: { label: 'В CRM', color: '#4caf50', bgColor: '#e8f5e9', icon: '✅', MuiIcon: ExportedIcon },
  not_target: { label: 'Не целевой', color: '#9e9e9e', bgColor: '#f5f5f5', icon: '❌', MuiIcon: BlockIcon },
  already_client: { label: 'Уже клиент', color: '#7c4dff', bgColor: '#ede7f6', icon: '🤝', MuiIcon: ClientIcon },
  competitor: { label: 'Конкурент', color: '#e91e63', bgColor: '#fce4ec', icon: '⚔️', MuiIcon: CompetitorIcon },
  'для розничного отдела': { label: 'Для розничного отдела', color: '#00897b', bgColor: '#e0f2f1', icon: '🏬', MuiIcon: RetailIcon },
  'для отдела КАМов': { label: 'Для отдела КАМов', color: '#3f51b5', bgColor: '#e8eaf6', icon: '👥', MuiIcon: KamIcon },
};

// Экспортируем для использования в фильтрах
export { STATUS_CONFIG };

const CompanyCard = ({ company, index, searchMetadata, companyStatus, onStatusChange }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Получаем идентификатор компании (inn или internal_id)
  const companyId = getCompanyIdentifier(company);
  const identifier = companyId?.value;

  // Получаем конфигурацию статуса
  const statusInfo = companyStatus?.status ? STATUS_CONFIG[companyStatus.status] : null;
  const isExported = companyStatus?.status === 'exported_to_crm';

  const handleStatusButtonClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusSelect = (statusKey) => {
    setSelectedStatus(statusKey);
    setNewComment(companyStatus?.comment || '');
    setCommentDialogOpen(true);
    handleMenuClose();
  };

  const handleSaveStatus = async () => {
    if (!identifier || !selectedStatus) return;

    setIsSaving(true);
    try {
      // Бэкенд требует comment обязательно, отправляем пустую строку если не заполнен
      const commentToSend = newComment.trim() || '';
      await setCompanyStatus(identifier, selectedStatus, commentToSend);
      
      // Уведомляем родительский компонент об изменении
      if (onStatusChange) {
        onStatusChange(identifier, {
          inn: company.inn,
          internal_id: company.internal_id,
          status: selectedStatus,
          comment: commentToSend,
          updated_at: new Date().toISOString()
        });
      }

      // Также отправляем глобальное событие
      window.dispatchEvent(new CustomEvent('exportStatusChanged', {
        detail: {
          companyIdentifier: identifier,
          companyInn: company.inn,
          companyInternalId: company.internal_id,
          status: selectedStatus,
          comment: commentToSend
        }
      }));

      setCommentDialogOpen(false);
      setSelectedStatus(null);
      setNewComment('');
    } catch (error) {
      console.error('Error saving status:', error);
      alert('Ошибка сохранения статуса: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveStatus = async () => {
    if (!identifier) return;
    
    handleMenuClose();
    setIsSaving(true);
    
    try {
      await deleteCompanyStatus(identifier);
      
      // Уведомляем родительский компонент об удалении статуса
      if (onStatusChange) {
        onStatusChange(identifier, null);
      }

      // Глобальное событие
      window.dispatchEvent(new CustomEvent('exportStatusChanged', {
        detail: {
          companyIdentifier: identifier,
          companyInn: company.inn,
          companyInternalId: company.internal_id,
          status: null,
          comment: null
        }
      }));
    } catch (error) {
      console.error('Error removing status:', error);
      alert('Ошибка снятия статуса: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClick = () => {
    let taskId = null;

    if (searchMetadata?.task_id) {
      taskId = searchMetadata.task_id;
    }

    if (!taskId && window.location.pathname.includes('/results/')) {
      const match = window.location.pathname.match(/\/results\/([^/]+)/);
      if (match) {
        taskId = match[1];
      }
    }

    if (!taskId) {
      taskId = sessionStorage.getItem('current_task_id');
    }

    if (taskId) {
      navigate(`/company/${index}?task_id=${taskId}`);
    } else {
      navigate(`/company/${index}`);
    }
  };

  const getCompanyData = (company) => {
    let phone = null;
    let hours = company.working_hours || null;
    let categories = company.categories || [];

    if (company.phone_numbers && company.phone_numbers.length > 0) {
      phone = company.phone_numbers[0];
    }

    if (company.stores && company.stores.length > 0) {
      const store = company.stores[0];

      if (!phone && store.phone_numbers && store.phone_numbers.length > 0) {
        phone = store.phone_numbers[0];
      }
      if (!hours && store.working_hours) {
        hours = store.working_hours;
      }
      if (categories.length === 0 && store.categories && store.categories.length > 0) {
        categories = store.categories;
      }
    }

    if (!phone && company.contacts && company.contacts.length > 0) {
      const phoneContact = company.contacts.find(c => c.type === 'phone' && c.value);
      if (phoneContact) phone = phoneContact.value;
    }

    // Fallback: phone from website_info.contact_info
    if (!phone && company.website_info?.contact_info?.phone) {
      phone = company.website_info.contact_info.phone;
    }

    return { phone, hours, categories };
  };

  const { phone, hours, categories } = getCompanyData(company);

  return (
    <>
      <div
        className={`company-card ${isExported ? 'company-card-exported' : ''} ${statusInfo ? `company-card-status-${companyStatus.status.replace(/\s+/g, '_')}` : ''}`}
        onClick={handleClick}
      >
        <div className="company-name">
          {company.name || 'Название не указано'}
        </div>

        {company.inn && (
          <div className="company-inn">ИНН: {company.inn}</div>
        )}

        {(() => {
          const addressData = createClickableAddressData(company.actual_address || company.address);
          return (
            <div className={`company-address ${addressData.isClickable ? 'company-address--clickable' : ''}`}>
              {addressData.isClickable ? (
                <a
                  href={addressData.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="company-address-link"
                  title="Открыть на Яндекс.Картах"
                  onClick={(e) => e.stopPropagation()}
                >
                  {addressData.text}
                </a>
              ) : (
                addressData.text
              )}
            </div>
          );
        })()}

        {phone && (
          <div className="company-phone">
            📞 {phone}
          </div>
        )}

        {hours && (
          <div className="company-hours">
            🕒 {hours}
          </div>
        )}

        {categories && categories.length > 0 && (
          <div className="company-categories">
            {categories.slice(0, 2).map((category, idx) => (
              <span key={idx} className="category-tag">
                {category}
              </span>
            ))}
            {categories.length > 2 && (
              <span className="category-tag">+{categories.length - 2}</span>
            )}
          </div>
        )}

        {/* Комментарий (если есть) */}
        {companyStatus?.comment && (
          <div className="company-comment" title={companyStatus.comment}>
            💬 {companyStatus.comment.length > 50 
              ? companyStatus.comment.substring(0, 50) + '...' 
              : companyStatus.comment}
          </div>
        )}

        {/* Кнопка статуса - всегда видна */}
        <div className="company-card-actions" onClick={(e) => e.stopPropagation()}>
          {statusInfo ? (
            // Если статус установлен - показываем кликабельный чип со статусом
            <Chip
              icon={<statusInfo.MuiIcon style={{ color: statusInfo.color }} />}
              label={statusInfo.label}
              deleteIcon={<ArrowDownIcon />}
              onDelete={handleStatusButtonClick}
              onClick={handleStatusButtonClick}
              sx={{
                backgroundColor: statusInfo.bgColor,
                color: statusInfo.color,
                fontWeight: 500,
                border: `1px solid ${statusInfo.color}`,
                cursor: 'pointer',
                '& .MuiChip-deleteIcon': {
                  color: statusInfo.color,
                },
                '&:hover': {
                  backgroundColor: statusInfo.bgColor,
                  filter: 'brightness(0.95)'
                }
              }}
            />
          ) : (
            // Если статуса нет - показываем кнопку "Установить статус"
            <Chip
              icon={<AddStatusIcon />}
              label="Установить статус"
              onClick={handleStatusButtonClick}
              variant="outlined"
              sx={{
                cursor: 'pointer',
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  borderColor: '#1565c0'
                }
              }}
            />
          )}
        </div>
      </div>

      {/* Меню выбора статуса */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem disabled sx={{ opacity: 1, fontWeight: 'bold', fontSize: '0.85em' }}>
          Выберите статус:
        </MenuItem>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <MenuItem 
            key={key} 
            onClick={() => handleStatusSelect(key)}
            selected={companyStatus?.status === key}
          >
            <ListItemIcon>
              <config.MuiIcon sx={{ color: config.color }} />
            </ListItemIcon>
            <ListItemText>{config.label}</ListItemText>
          </MenuItem>
        ))}
        
        {companyStatus?.status && (
          <>
            <Divider />
            <MenuItem onClick={() => handleStatusSelect(companyStatus.status)}>
              <ListItemIcon>
                <EditIcon />
              </ListItemIcon>
              <ListItemText>Изменить комментарий</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleRemoveStatus} disabled={isSaving}>
              <ListItemIcon>
                <RemoveStatusIcon color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Снять статус</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Диалог для ввода комментария */}
      <Dialog 
        open={commentDialogOpen} 
        onClose={() => setCommentDialogOpen(false)}
        onClick={(e) => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>
          {selectedStatus && STATUS_CONFIG[selectedStatus] && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {STATUS_CONFIG[selectedStatus].icon} {STATUS_CONFIG[selectedStatus].label}
            </span>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Комментарий (необязательно)"
            fullWidth
            multiline
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Например: Перезвонить после 15:00, интересует электроинструмент..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)} disabled={isSaving}>
            Отмена
          </Button>
          <Button 
            onClick={handleSaveStatus} 
            variant="contained" 
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : null}
          >
            {isSaving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyCard;
